import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Card, Alert, Spinner, Row, Col, Modal } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiCall from './../../API/API'; // Adjust this path if needed

const TwoFactorAuthSetup = () => {
  // State to manage the 2FA status and data
  const [is2faEnabled, setIs2faEnabled] = useState(false); // True if 2FA is currently active for the user
  const [showSetup, setShowSetup] = useState(false); // True if QR code/OTP input is visible
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false); 
  const [successMessage, setSuccessMessage] = useState(null);
  const [otpVerificationLoading, setOtpVerificationLoading] = useState(false); // Loading state for verifying OTP
  const [otpError, setOtpError] = useState(null);

  // Function to check initial 2FA status when the component mounts
  useEffect(() => {
    const check2FAStatus = async () => {
      setLoading(true);
      try {
        const { data, error: apiError } = await apiCall('GET', '/check_2fa_status'); // Assuming an endpoint to check 2FA status
        if (apiError) {
          toast.error(apiError.message || 'Failed to fetch 2FA status.');
        } else if (data && typeof data.isEnabled === 'boolean') {
          console.log("status---------999", data);
          setIs2faEnabled(data.isEnabled);
        }
      } catch (err) {
        toast.error('An error occurred while fetching 2FA status.');
        console.error('2FA Status Fetch Error:', err);
      } finally {
        setLoading(false);
      }
    };
    check2FAStatus();
  }, []); // Run once on component mount

  // Function to initiate 2FA setup (get QR code and secret)
  const handleEnableButtonClick = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // API call to your backend to get 2FA setup data
      const { data, error: apiError } = await apiCall('GET', '/setup_2fa'); // Adjust endpoint as per your backend
      
      if (apiError) {
        setError(apiError.message || 'Failed to get 2FA setup data.');
        toast.error(apiError.message || 'Failed to get 2FA setup data.');
      } else if (data) {
        console.log("-----", data);
        setQrCodeUrl(data.qrCode);
        setSecretKey(data.secret);
        setShowSetup(true); // Show the setup UI
        toast.success('2FA setup initiated. Please scan the QR code.');
      } else {
        setError('Unexpected response from 2FA enable API.');
        toast.error('Unexpected response from 2FA enable API.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while enabling 2FA.');
      toast.error(err.message || 'An error occurred while enabling 2FA.');
      console.error('2FA Enable Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to verify OTP and activate 2FA
  const handleVerifyOtp = async () => {
    if (otp.length !== 6 || !/^\d+$/.test(otp)) {
      setError('Please enter a valid 6-digit OTP.');
      toast.error('Please enter a valid 6-digit OTP.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // API call to your backend to verify the OTP
      // Ensure your backend verifies 'otp' against the 'secretKey' that was generated for the user
      const { data, error: apiError } = await apiCall('POST', '/verify_2fa', { token: otp }); // Adjust endpoint and payload
      
      if (apiError) {
        setError(apiError.message || 'OTP verification failed.');
        toast.error(apiError.message || 'OTP verification failed.');
      } else if (data) { // Assuming backend sends { success: true, message: '...' }
        setIs2faEnabled(true); // Set 2FA as enabled
        setShowSetup(false); // Hide the setup UI
        setQrCodeUrl(''); // Clear setup data as it's no longer needed
        setSecretKey('');
        setOtp(''); // Clear OTP field
        setSuccessMessage(data.message || '2FA Activated successfully!');
        toast.success(data.message || '2FA Activated successfully!');
      } else {
        setError('OTP verification failed. Please try again.');
        toast.error('OTP verification failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during OTP verification.');
      toast.error(err.message || 'An error occurred during OTP verification.');
      console.error('OTP Verification Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    // Instead of window.confirm, just show the modal
    setShowDisable2FAModal(false);
    setOtp('');
  };

  const handleConfirmDisable2FA = async () => {
    setShowDisable2FAModal(false); // Close the modal immediately

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    console.log("irufurojrklfj", otp);
    try {
      // Assuming your backend has an endpoint to disable 2FA
      const { data, error: apiError } = await apiCall('POST', '/disable_2fa', {token: otp});

      if (apiError) {
        setError(apiError.message || 'Failed to disable 2FA.');
        toast.error(apiError.message || 'Failed to disable 2FA.');
        setOtp('');
      } else if (data) {
        setIs2faEnabled(false);
        setSuccessMessage(data.message || '2FA has been successfully disabled.');
        toast.success(data.message || '2FA has been successfully disabled.');
        setOtp('');
      } else {
        setError('Failed to disable 2FA. Please try again.');
        toast.error('Failed to disable 2FA. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while disabling 2FA.');
      toast.error(err.message || 'An error occurred while disabling 2FA.');
      console.error('2FA Disable Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-md-center">
        <Col md={8} lg={6}>
          <Card className="p-4 shadow-sm">
            <Card.Title className="text-center mb-4">Two-Factor Authentication (2FA)</Card.Title>

            {/* General message alerts */}
            {error && <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-3">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible className="mb-3">{successMessage}</Alert>}

            {/* Loading Spinner */}
            {loading && (
              <div className="d-flex justify-content-center my-3">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            )}

            {/* State 1: Initial / 2FA Disabled */}
            {/* Show this if 2FA is not enabled AND not in the setup phase AND not loading */}
            {!is2faEnabled && !showSetup && !loading && (
              <div className="text-center">
                <p className="mb-3">Two-Factor Authentication is currently <u>disabled</u> for your account.</p>
                <Button variant="primary" onClick={handleEnableButtonClick} disabled={loading}>
                  Enable 2FA
                </Button>
              </div>
            )}

            {/* State 2: QR Code & Verification (2FA setup in progress) */}
            {/* Show this if 2FA is not enabled AND in the setup phase AND not loading */}
            {!is2faEnabled && showSetup && !loading && (
              <div className="text-center">
                <p className="mb-3">
                  <u>Scan the QR code</u> below with your authenticator app (e.g., Google Authenticator, Authy).
                </p>
                <p className="mb-4">
                  Alternatively, manually <u>enter the secret key</u> into your app.
                </p>

                {qrCodeUrl && (
                  <div className="my-4">
                    <img
                      src={qrCodeUrl}
                      alt="2FA QR Code"
                      className="border p-2"
                      style={{ maxWidth: '200px', height: 'auto', borderRadius: '5px' }}
                    />
                  </div>
                )}

                {secretKey && (
                  <div className="mb-4">
                    <p>
                      <strong>Secret Key:</strong>{' '}
                      <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#007bff', background: '#e9f5ff', padding: '5px 10px', borderRadius: '4px', display: 'inline-block', wordBreak: 'break-all' }}>
                        {secretKey}
                      </span>
                      {/* You could add a copy button here */}
                    </p>
                  </div>
                )} 

                <Form.Group className="mb-3 w-75 mx-auto">
                  <Form.Label htmlFor="otpInput">Enter 6-digit OTP from your Authenticator App</Form.Label>
                  <Form.Control
                    id="otpInput"
                    type="text" // Use text to allow for easier input validation and prevent leading zeros issues
                    inputMode="numeric" // Helps mobile keyboards show numeric input
                    pattern="[0-9]{6}" // HTML5 pattern for 6 digits
                    maxLength="6"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="e.g., 123456"
                    isInvalid={!!error} // Apply Bootstrap invalid styling if there's an error
                  />
                  {/* Bootstrap provides invalid feedback via Form.Control.Feedback when isInvalid is true */}
                  <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
                </Form.Group>

                <Button variant="success" onClick={handleVerifyOtp} disabled={loading}>
                  Verify & Activate 2FA
                </Button>
              </div>
            )}

            {/* State 3: 2FA Activated */}
            {is2faEnabled && !loading && (
              <div className="text-center">
                <p className="lead text-success mb-3">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  Two-Factor Authentication is Activated!
                </p>
                <p>Your account is now protected with enhanced security.</p>

                <Button variant="danger" onClick={() => setShowDisable2FAModal(true)} className="mt-3" disabled={loading}>
                  Disable 2FA
                </Button>
              </div>
            )}
          </Card>
        </Col>
        <Modal show={showDisable2FAModal} onHide={() => setShowDisable2FAModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Disable Two-Factor Authentication</Modal.Title>
          </Modal.Header>
          {/* <Modal.Body>
            <p>Are you sure you want to disable Two-Factor Authentication?</p>
            <p className="text-danger">Disabling 2FA will reduce your account's security.</p>
          </Modal.Body> */}
            <Modal.Body>
            <p>Please enter the 6-digit OTP sent to your registered email or phone to confirm disabling 2FA.</p>
            <p className="text-danger">Disabling 2FA will reduce your account's security.</p>

            {otpError && <Alert variant="danger">{otpError}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label htmlFor="otpInput">One-Time Password (OTP)</Form.Label>
              <Form.Control
                type="text"
                id="otpInput"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                isInvalid={!!otpError}
                maxLength={6} // Enforce 6 digits
                inputMode="numeric" // Suggest numeric keyboard on mobile
                pattern="[0-9]{6}" // HTML5 pattern for 6 digits
                disabled={otpVerificationLoading}
              />
              <Form.Control.Feedback type="invalid">
                {otpError}
              </Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDisable2FA} disabled={loading}>
              Disable
            </Button>
          </Modal.Footer>
        </Modal>
      </Row>
      {/* <ToastContainer position="bottom-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover /> */}
    </Container>
  );
};

export default TwoFactorAuthSetup;